import { ArrowDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { cn } from '@/lib/utils';
import { useCopilotChat } from '@/modules/copilot/useCopilotChat';
import { Button } from '@/modules/ui/components/button/Button';
import { Sheet, SheetContent } from '@/modules/ui/components/sheet/Sheet';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';

import { CopilotConversation } from './CopilotConversation';
import { CopilotHeader } from './CopilotHeader';
import { CopilotSender } from './CopilotSender';
import { EmptyState } from './EmptyState';

interface CopilotProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const Copilot = ({ open, onOpenChange }: CopilotProps) => {
  const trackEvent = useTrackEvent();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const {
    messages,
    isRequesting,
    sendMessage,
    startNewSession,
    retryLastMessage,
  } = useCopilotChat();

  const currentUserQuery = useCurrentUserQuery();
  const userName =
    `${currentUserQuery.data?.firstName ?? ''} ${currentUserQuery.data?.lastName ?? ''}`.trim() ||
    'User';

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      const maxScroll =
        scrollContainer.scrollHeight - scrollContainer.clientHeight;
      scrollContainer.scrollTo({
        top: maxScroll,
        behavior,
      });
    }
  };

  const handleSubmit = (userMessage: string) => {
    sendMessage(userMessage);
    trackEvent('copilotMessageSent', {
      message: userMessage,
    });
  };

  const messagesWithKey = messages.map((message) => ({
    key: message.id,
    ...message,
  }));

  const isChatActive = messagesWithKey.length > 0;

  useEffect(() => {
    if (isAtBottom) {
      const timeoutId = setTimeout(() => scrollToBottom('smooth'), 50);
      return () => clearTimeout(timeoutId);
    }
  }, [messages, isAtBottom]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const bottomThreshold = 100;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

      setIsAtBottom(distanceFromBottom < bottomThreshold);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [isChatActive]);

  const bgColor = 'bg-[#FAFAFF]';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        hideCloseButton
        className={cn(
          'flex w-full flex-col gap-4 p-0 sm:max-w-[498px]',
          isChatActive ? 'bg-white' : bgColor,
        )}
      >
        <CopilotHeader
          isChatActive={isChatActive}
          onNewSession={startNewSession}
          onClose={() => onOpenChange(false)}
        />

        {isChatActive ? (
          <div
            ref={scrollContainerRef}
            className="min-h-0 flex-1 overflow-y-auto pb-[88px]"
          >
            <CopilotConversation
              messages={messagesWithKey}
              userName={userName}
              onRetry={retryLastMessage}
            />
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto pb-[88px]">
            <EmptyState />
          </div>
        )}

        {!isAtBottom && isChatActive && (
          <div className="pointer-events-none absolute right-0 bottom-[104px] left-0 flex justify-center">
            <Button
              variant="outline"
              className="pointer-events-auto shadow-lg"
              onClick={() => scrollToBottom('smooth')}
            >
              <ArrowDown className="h-4 w-4" />
            </Button>
          </div>
        )}

        <div
          className={cn(
            'absolute right-0 bottom-0 left-0 z-1000 px-4 py-4',
            isChatActive && bgColor,
          )}
        >
          <CopilotSender
            onSubmit={handleSubmit}
            onCancel={() => {}}
            isLoading={isRequesting}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};
